import { UpdateBeaconFundRequestValidation, useListBeaconFundRequest, useUpdateBeaconRequest, useDeleteBeaconRequest, CreateBeaconFundRequestValidation } from "@/lib/api/fund";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { CommonDestinationTimezone, formatCurrency, GenerateLoadingOrError } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { safeParseDecimal } from "@/lib/api/common";
import { getReadableStatusLabel, getStatusBadgeColor } from "../common";
import { cn } from "@/lib/utils";
import { useListCustomers } from "@/lib/api/customer";
import { useListMinimalUsers } from "@/lib/api/users";
import { DeleteConfirmationDialog } from "./components/DeleteConfirmationDialog";
import { Building, Users, Mail, Phone, Calendar, DollarSign, FileText } from "lucide-react";
import { useQueryParam } from "@/hooks/use-query-param";
import { BeaconFundRequestForm } from "./components/BeaconFundRequestForm";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

type FormData = z.infer<typeof CreateBeaconFundRequestValidation>;

export function BeaconFundRequestModal() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [request_id, setRequestId] = useQueryParam("request_id", "");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const requestResult = useListBeaconFundRequest(request_id);
  const updateRequest = useUpdateBeaconRequest();
  const minimalCustomersListResult = useListCustomers({ minimal: true });
  const minimalUserListResult = useListMinimalUsers();

  const request = requestResult.data?.data;

  const form = useForm<FormData>({
    resolver: zodResolver(CreateBeaconFundRequestValidation),
    defaultValues: {
      survey: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        matchingGiftAmount: "",
        recipientOrganization: "",
        recipientOrganizationMailingAddress: "",
        someOrAllMatchingConfirmation: "",
        organizationPurpose: "",
      },
      has_been_granted: false,
      has_been_dispersed: false,
      amount_granted: undefined,
      customer_id: null,
      employee_id: null,
    },
  })

  useEffect(() => {
    if (request) {
      form.reset({
        survey: {
          firstName: request.survey?.data?.firstName || "",
          lastName: request.survey?.data?.lastName || "",
          phone: request.survey?.data?.phone || "",
          email: request.survey?.data?.email || "",
          matchingGiftAmount: request.survey?.data?.matchingGiftAmount || "",
          recipientOrganization: request.survey?.data?.recipientOrganization || "",
          recipientOrganizationMailingAddress: request.survey?.data?.recipientOrganizationMailingAddress || "",
          someOrAllMatchingConfirmation: request.survey?.data?.someOrAllMatchingConfirmation || "",
          organizationPurpose: request.survey?.data?.organizationPurpose || "",
        },
        has_been_granted: request.has_been_granted || false,
        has_been_dispersed: request.has_been_dispersed || false,
        amount_granted: request.amount_granted ? parseFloat(request.amount_granted) : undefined,
        customer_id: request.customer_id || null,
        employee_id: request.employee_id || null,
        customer_requested: request.customer_requested || null,
      });
    }
  }, [request_id, request, form]);

  const handleClose = () => {
      setRequestId("");
      setIsEditing(false);
  };

  const onSubmit = async (data: FormData) => {
    if (!request_id || !request || !isEditing || isSaving) return;
    
    setIsSaving(true);

    try {
      const parsedResult = UpdateBeaconFundRequestValidation.safeParse({
        id: request_id,
        survey: data.survey,
        customer_id: data.customer_id || null,
        employee_id: data.employee_id || null,
        customer_requested: data.customer_requested || null,
        has_been_granted: data.has_been_granted,
        has_been_dispersed: data.has_been_dispersed,
        amount_granted: data.amount_granted ? data.amount_granted.toString() : undefined
      });
      if (!parsedResult.success) {
        toast({
          title: "Invalid request",
          description: "",
        });
        return;
      }

      const updateResult = await updateRequest(parsedResult.data);

      if (!updateResult.success) {
        throw new Error("Update failed");
      }

      toast({ title: "Request updated successfully" });
      setIsEditing(false);
    } catch (error: any) {
      console.error("Failed to update form", error);
      toast({ 
        title: "Failed to update request",
        variant: "destructive" 
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!request_id) return null;

  const isOpen = !!request_id;
  const loadingOrErrorUi = GenerateLoadingOrError([requestResult, minimalCustomersListResult]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={open => {
        if (!open) { handleClose() }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          {loadingOrErrorUi ? (
            loadingOrErrorUi
          ) : !request ? (
            <div className="p-4 text-center">Request not found</div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Beacon Fund Request Details</span>
                </DialogTitle>
                <DialogDescription>
                  Review and manage beacon fund request
                </DialogDescription>
              </DialogHeader>

            {!isEditing && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Survey Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-sm text-muted-foreground">Recipient Name</Label>
                      <p className="font-medium">
                        {request.survey?.data?.firstName || ""} {request.survey?.data?.lastName || ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label className="text-sm text-muted-foreground">Email</Label>
                        <p>{request.survey?.data?.email || "Not provided"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label className="text-sm text-muted-foreground">Phone</Label>
                        <p>{request.survey?.data?.phone || "Not provided"}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Recipient Organization</Label>
                      <p className="font-medium">{request.survey?.data?.recipientOrganization || "Not specified"}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Mailing Address</Label>
                      <p className="text-sm">{request.survey?.data?.recipientOrganizationMailingAddress || "Not provided"}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Confirmed</Label>
                      <p className="text-sm">{request.survey?.data?.someOrAllMatchingConfirmation || "Not provided"}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Purpose</Label>
                      <p className="text-sm">{request.survey?.data?.organizationPurpose || ""}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Request Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-sm text-muted-foreground">Assigned To</Label>
                      {request.Customer && (
                        <div className="flex items-center gap-2 mt-1">
                          <Building className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{request.Customer?.long_name}</span>
                        </div>
                      )}
                      {request.Employee && (
                        <div className="flex items-center gap-2 mt-1">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{request.Employee?.name}</span>
                        </div>
                      )}
                      {request.customer_requested && (
                        <div className="flex items-center gap-2 mt-1">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{request.customer_requested}</span>
                        </div>
                      )}
                      {!request.Customer && !request.Employee && !request.customer_requested && (
                        <p className="text-muted-foreground">Not assigned</p>
                      )}
                    </div>
                    <div className="grid">
                      <Label className="text-sm text-muted-foreground">Status</Label>
                      <Badge className={cn("mt-1", getStatusBadgeColor(request.status))}>
                        {getReadableStatusLabel(request.status)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid">
                        <Label className="text-sm text-muted-foreground">Request Granted</Label>
                        <span className="mt-1">
                          <b>{request.has_been_granted ? "Yes" : "No"}</b>
                        </span>
                      </div>
                      <div className="grid">
                        <Label className="text-sm text-muted-foreground">Funds Disbursed</Label>
                        <span className="mt-1">
                          <b>{request.has_been_dispersed ? "Yes" : "No"}</b>
                        </span>
                      </div>
                    </div>
                    {request.has_been_granted && (
                      <div>
                        <Label className="text-sm text-muted-foreground">Amount Granted</Label>
                        <p className="font-medium text-lg">
                          {request.amount_granted ? formatCurrency(parseFloat(request.amount_granted)) : "-"}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label className="text-sm text-muted-foreground">Created</Label>
                        <p>{moment.tz(request.created_at, CommonDestinationTimezone).format("MM/DD/YYYY")}</p>
                      </div>
                    </div>
                    {request.granted_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <Label className="text-sm text-muted-foreground">Granted Date</Label>
                          <p>{moment.tz(request.granted_date, CommonDestinationTimezone).format("MM/DD/YYYY")}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

              {isEditing && (
                <Card className="my-4">
                  <CardHeader>
                    <CardTitle>Edit Request</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BeaconFundRequestForm
                      form={form}
                      onSubmit={onSubmit}
                      isLoading={isSaving}
                      isEditMode={true}
                      onCancel={() => setIsEditing(false)}
                    />
                  </CardContent>
                </Card>
              )}

              {request.survey?.errors && request.survey.errors.length > 0 && (
                <Card className="my-4 border-destructive/50 bg-destructive/10">
                  <CardHeader>
                    <CardTitle className="text-destructive">Survey Errors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc pl-5 space-y-1">
                      {request.survey.errors.map((error: any, index: number) => (
                        <li key={index} className="text-destructive">
                          {typeof error === 'string' ? error : (
                            <>
                              {error.field && <span className="font-medium">{error.field}: </span>}
                              {error.error || JSON.stringify(error)}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {!isEditing && (
                <DialogFooter>
                  <div className="flex gap-2">
                      <>
                        <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                          Edit
                        </Button>
                      </>
                  </div>
                  <Button variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
